import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import type { FilterDefinition, FilterInstanceParts } from "../../types/filterDefinition";
import type { SearchFilterInstance } from "../../types/searchFilterInstance";

export type DefineFilterInput<
    TValues extends IPlainObject,
    TApiCategory extends string,
    TCtx,
    TDataSource extends string,
    TCapability extends string,
    K extends Extract<keyof TValues, string>,
> = Omit<FilterDefinition<TValues, TApiCategory, TCtx, TDataSource, TCapability, K>, "build"> & {
    /**
     * The runtime parts of the instance. Identity is filled in from the fields above (a returned value may
     * still override `title`); a fan-out returns full, self-identified instances; `undefined` is a veto.
     */
    readonly render: (
        ctx: TCtx,
    ) =>
        | FilterInstanceParts<TValues, TApiCategory>
        | readonly SearchFilterInstance<TValues, TApiCategory>[]
        | undefined;
};

/**
 * Authors a `FilterDefinition` so identity is declared once. `render` returns only the runtime parts; the
 * generated `build` injects `key`/`apiCategory`/`category`/`trackingName`/`title` from the declaration
 * (letting `render` override `title`), passes a fan-out array through unchanged (its members carry their own
 * identity), and returns `undefined` for a veto. Because `render`'s single-return type omits the identity
 * fields, restating them there is a compile error — drift is not just discouraged, it does not compile.
 */
export const defineFilter = <
    TValues extends IPlainObject,
    TApiCategory extends string,
    TCtx,
    TDataSource extends string,
    TCapability extends string,
    K extends Extract<keyof TValues, string>,
>({
    render,
    ...identity
}: DefineFilterInput<TValues, TApiCategory, TCtx, TDataSource, TCapability, K>): FilterDefinition<
    TValues,
    TApiCategory,
    TCtx,
    TDataSource,
    TCapability,
    K
> => ({
    ...identity,
    build: (ctx) => {
        const rendered = render(ctx);

        if (rendered == null) {
            return undefined;
        }
        // A fan-out returns full, self-identified instances; only single renders get identity injected.
        if (Array.isArray(rendered)) {
            return rendered;
        }

        // The runtime shape (identity + parts) is a complete instance; the cast is only because
        // `SearchFilterInstance<TValues, …>` is a conditional over an abstract `TValues` the compiler
        // cannot verify structurally — the same reason concrete definitions annotate their instance literal.
        return {
            key: identity.key,
            apiCategory: identity.apiCategory,
            category: identity.category,
            trackingName: identity.trackingName,
            title: identity.titleKey,
            ...(rendered as FilterInstanceParts<TValues, TApiCategory>),
        } as unknown as SearchFilterInstance<TValues, TApiCategory>;
    },
});
